import { forwardRef } from "react";
import clsx from "clsx";
import type { FormInputProps } from "./formInput.types";
import {
  getFormInputContainerClasses,
  getFormInputLabelClasses,
  getFormInputClasses,
  getIconContainerClasses,
  getErrorMessageClasses,
  getHelperTextClasses,
} from "./formInput.definitions";

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      required = false,
      icon,
      helperText,
      className,
      inputClassName,
      id,
      ...props
    },
    ref
  ) => {
    const inputId =
      id || `form-input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = Boolean(error);
    const hasIcon = Boolean(icon);

    return (
      <div className={getFormInputContainerClasses(className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={getFormInputLabelClasses(required, hasError)}
          >
            {label}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Ícone (se fornecido) */}
          {icon && <div className={getIconContainerClasses()}>{icon}</div>}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              getFormInputClasses(hasError, hasIcon),
              inputClassName
            )}
            aria-invalid={hasError}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                ? `${inputId}-helper`
                : undefined
            }
            {...props}
          />
        </div>

        {/* Mensagem de Erro */}
        {error && (
          <p
            id={`${inputId}-error`}
            className={getErrorMessageClasses()}
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Texto de Ajuda */}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className={getHelperTextClasses()}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = "FormInput";

export default FormInput;
